import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { addMakerPlan } from "../../../services/adminServices/MakerPlanServices";

const INITIAL_FORM_STATE = { makerPlan: "", codingHours: "", description: "" };
const INITIAL_ERRORS_STATE = {};

const MakerPlanModal = ({ open, onClose,fetchMakerPlans }) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState(INITIAL_ERRORS_STATE);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) resetFormState();
  }, [open]);

  const resetFormState = () => {
    setFormValues(INITIAL_FORM_STATE);
    setFormErrors(INITIAL_ERRORS_STATE);
    setIsLoading(false);
  };

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "makerPlan" || name === "description") {
      setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
      return;
    }

    const numericValue = value.replace(/[^0-9.]/g, "");

    if (numericValue !== value) {
      setFormErrors({
        ...formErrors,
        [name]: "Please enter valid numbers only",
      });
    } else {
      setFormErrors({ ...formErrors, [name]: "" });
    }

    setFormValues((prevValues) => ({ ...prevValues, [name]: numericValue }));
  };

  const validateFields = () => {
    const errors = {};
    const { makerPlan, codingHours, description } = formValues;

    if (!makerPlan) errors.makerPlan = "Maker Plan is required*";
    if (!codingHours) errors.codingHours = "Coding Hours is required*";
    if (!description) errors.description = "Description is required*";

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const obj = {
      makerplan_Name: formValues.makerPlan,
      coding_Hrs: parseInt(formValues.codingHours),
      description: formValues.description,
    };
    setIsLoading(true);
    try {
      const response = await addMakerPlan(obj);
      toast.success(response.data.message);
      fetchMakerPlans()
      resetFormState();
      onClose();
    } catch (error) {
      toast.error( error?.response?.data.message||error?.message ||error?.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "45%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Create Maker Plan
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {[
              {
                label: "Maker Plan *",
                name: "makerPlan",
                type: "text",
                error: formErrors.makerPlan,
              },
              {
                label: "Coding Hours *",
                name: "codingHours",
                type: "text",
                error: formErrors.codingHours,
              },
              {
                label: "Description *",
                name: "description",
                type: "textarea",
                error: formErrors.description,
              },
            ].map(({ label, name, type, error, ...props }) => (
              <Grid item xs={12} key={name}>
                {type === "textarea" ? (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label={label}
                    name={name}
                    value={formValues[name]}
                    onChange={handleInputChange}
                    error={!!error}
                    helperText={error}
                    {...props}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    type={type}
                    label={label}
                    name={name}
                    value={formValues[name]}
                    onChange={handleInputChange}
                    error={!!error}
                    helperText={error}
                    {...props}
                  />
                )}
              </Grid>
            ))}

            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                sx={{ marginRight: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Submit"}
              </Button>
              <Button variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

export default MakerPlanModal;
